import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { AdminActionsProvider } from './context/AdminActionsContext';
import ProtectedRoute from './components/ProtectedRoute';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Content from './pages/Content';
import Sellers from './pages/Sellers';
import Authors from './pages/Authors';
import DataDirectory from './pages/DataDirectory';
import OfficialCircles from './pages/OfficialCircles';
import OfficialCircleCommunityModeration from './pages/OfficialCircleCommunityModeration';
import Trivia from './pages/Trivia';
import Brain from './pages/Brain';
import Stories from './pages/Stories';
import Marketplace from './pages/Marketplace';
import Analytics from './pages/Analytics';
import Flags from './pages/Flags';
import Notifications from './pages/Notifications';
import AuditLog from './pages/AuditLog';
import BooksPage from './pages/BooksPage';
import Feedback from './pages/Feedback';
import Subscriptions from './pages/Subscriptions';
import Corrections from './pages/Corrections';
import SinhalaCorrections from './pages/SinhalaCorrections';
import ExchangeDisputes from './pages/ExchangeDisputes';
import WisdomReports from './pages/WisdomReports';
import AccountSecurity from './pages/AccountSecurity';

// ── Layout Wrapper (Sidebar + Header + Content) ──────────────────────
const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-cream">
    <Sidebar />
    <Header />
    <main className="ml-[240px] pt-16">
      <div className="p-8">
        {children}
      </div>
    </main>
  </div>
);

// ── Protected Page Wrapper ───────────────────────────────────────────
const ProtectedPage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ProtectedRoute>
    <AppLayout>
      {children}
    </AppLayout>
  </ProtectedRoute>
);

// ── App Component ────────────────────────────────────────────────────
const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AdminActionsProvider>
        <Routes>
          {/* Public route */}
          <Route path="/login" element={<Login />} />

          {/* Protected routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedPage>
                <Dashboard />
              </ProtectedPage>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedPage>
                <Users />
              </ProtectedPage>
            }
          />

          {/* Coming Soon pages */}
          <Route
            path="/content"
            element={
              <ProtectedPage>
                <Content />
              </ProtectedPage>
            }
          />
          <Route
            path="/sellers"
            element={
              <ProtectedPage>
                <Sellers />
              </ProtectedPage>
            }
          />
          <Route
            path="/authors"
            element={
              <ProtectedPage>
                <Authors />
              </ProtectedPage>
            }
          />
          <Route
            path="/data-directory"
            element={
              <ProtectedPage>
                <DataDirectory />
              </ProtectedPage>
            }
          />
          <Route
            path="/official-circles"
            element={
              <ProtectedPage>
                <OfficialCircles />
              </ProtectedPage>
            }
          />
          <Route
            path="/official-circles/community"
            element={
              <ProtectedPage>
                <OfficialCircleCommunityModeration />
              </ProtectedPage>
            }
          />
          <Route
            path="/trivia"
            element={
              <ProtectedPage>
                <Trivia />
              </ProtectedPage>
            }
          />
          <Route
            path="/brain"
            element={
              <ProtectedPage>
                <Brain />
              </ProtectedPage>
            }
          />
          <Route
            path="/stories"
            element={
              <ProtectedPage>
                <Stories />
              </ProtectedPage>
            }
          />
          <Route
            path="/marketplace"
            element={
              <ProtectedPage>
                <Marketplace />
              </ProtectedPage>
            }
          />
          <Route
            path="/exchange-disputes"
            element={
              <ProtectedPage>
                <ExchangeDisputes />
              </ProtectedPage>
            }
          />
          <Route
            path="/wisdom-reports"
            element={
              <ProtectedPage>
                <WisdomReports />
              </ProtectedPage>
            }
          />
          <Route
            path="/analytics"
            element={
              <ProtectedPage>
                <Analytics />
              </ProtectedPage>
            }
          />

          <Route
            path="/flags"
            element={
              <ProtectedPage>
                <Flags />
              </ProtectedPage>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedPage>
                <Notifications />
              </ProtectedPage>
            }
          />

          <Route
            path="/audit-log"
            element={
              <ProtectedPage>
                <AuditLog />
              </ProtectedPage>
            }
          />
          <Route
            path="/account/security"
            element={
              <ProtectedPage>
                <AccountSecurity />
              </ProtectedPage>
            }
          />
          <Route
            path="/feedback"
            element={
              <ProtectedPage>
                <Feedback />
              </ProtectedPage>
            }
          />
          <Route
            path="/books"
            element={
              <ProtectedPage>
                <BooksPage />
              </ProtectedPage>
            }
          />
          <Route
            path="/subscriptions"
            element={
              <ProtectedPage>
                <Subscriptions />
              </ProtectedPage>
            }
          />
          <Route
            path="/corrections"
            element={
              <ProtectedPage>
                <Corrections />
              </ProtectedPage>
            }
          />
          <Route
            path="/corrections/sinhala"
            element={
              <ProtectedPage>
                <SinhalaCorrections />
              </ProtectedPage>
            }
          />

          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        </AdminActionsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
